import React, { useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Grid,
  TextField,
  Typography,
  Divider,
  InputAdornment,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  alpha,
  useTheme,
  Autocomplete,
  ToggleButtonGroup,
  ToggleButton
} from '@mui/material';
import {
  AttachMoney as MoneyIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import { supabase } from '../../../../supabaseClient';
import { SectionPaper, SectionTitle, TotalRow, NumberTextField, SummaryValue, StatusChip } from './styledComponents';
import { formatCurrency } from './utils';

const PISummarySection = ({
  purchaseOrder,
  onUpdate,
  canEdit = true,
  poId: propPoId,
  showDetails = true,
  showSummary = true,
  showPaymentTerms = true,
  showNotes = true
}) => {
  const theme = useTheme();
  const discountType = purchaseOrder.discount_type || 'percent';
  const { poId: urlPoId } = useParams();
  const poId = propPoId || urlPoId; // Use prop first, fallback to URL
  const hasFetchedPayment = useRef(false);

  // Auto-fetch payment terms + method from order creation on mount
  useEffect(() => {
    const poNumber = purchaseOrder?.po_number || purchaseOrder?.customer_po_number;
    if (!poNumber || hasFetchedPayment.current) return;
    hasFetchedPayment.current = true;

    const fetchPaymentData = async () => {
      try {
        const { data: poData, error: poError } = await supabase
          .from('customer_purchase_orders')
          .select('id')
          .eq('po_number', poNumber)
          .single();

        if (poError || !poData?.id) return;

        const { data, error } = await supabase
          .from('po_order_creation')
          .select('payment_terms, payment_method')
          .eq('purchase_order_id', poData.id)
          .single();

        if (error && error.code !== 'PGRST116') return;

        if (data) {
          const updates = {};
          if (data.payment_terms) updates.payment_terms = data.payment_terms;
          if (data.payment_method) updates.payment_method = data.payment_method;
          if (Object.keys(updates).length > 0) onUpdate(updates);
        }
      } catch (err) {
        // silent fail
      }
    };

    fetchPaymentData();
  }, [purchaseOrder.po_number, purchaseOrder.customer_po_number]);

  return (
    <>
      {showDetails && (
      <SectionPaper sx={{ width: '100%', mt: 3 }}>
        <SectionTitle variant="h6">
          <MoneyIcon />
          PI Details
        </SectionTitle>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label="PI Number"
              value={purchaseOrder.po_number || ''}
              onChange={(e) => canEdit && onUpdate({ po_number: e.target.value })}
              fullWidth
              required
              margin="normal"
              disabled={true}
              InputProps={{
                sx: {
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  background: 'white',
                  opacity: 0.6,
                  '& fieldset': { border: 'none' }
                }
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth margin="normal">
              <InputLabel>Status</InputLabel>
              <Select
                value={purchaseOrder.po_status || 'draft'}
                onChange={(e) => canEdit && onUpdate({ po_status: e.target.value })}
                label="Status"
                disabled={!canEdit}
                sx={{
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  background: 'white',
                  '& .MuiOutlinedInput-notchedOutline': { border: 'none' }
                }}
              >
                <MenuItem value="draft">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <StatusChip label="Draft" status="draft" size="small" />
                    Draft
                  </Box>
                </MenuItem>
                <MenuItem value="pending_approval">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <StatusChip label="Pending" status="pending_approval" size="small" />
                    Pending Approval
                  </Box>
                </MenuItem>
                <MenuItem value="approved">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <StatusChip label="Approved" status="approved" size="small" />
                    Approved
                  </Box>
                </MenuItem>
                <MenuItem value="sent_to_customer">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <StatusChip label="Sent" status="sent_to_customer" size="small" />
                    Sent to Customer
                  </Box>
                </MenuItem>
                <MenuItem value="acknowledged">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <StatusChip label="Acknowledged" status="acknowledged" size="small" />
                    Acknowledged
                  </Box>
                </MenuItem>
                <MenuItem value="in_production">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <StatusChip label="In Production" status="in_production" size="small" />
                    In Production
                  </Box>
                </MenuItem>
                <MenuItem value="cancelled">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <StatusChip label="Cancelled" status="cancelled" size="small" />
                    Cancelled
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="PI Date"
                value={purchaseOrder.po_date ? dayjs(purchaseOrder.po_date) : null}
                onChange={(newValue) => canEdit && onUpdate({ po_date: newValue ? newValue.format('YYYY-MM-DD') : '' })}
                disabled={!canEdit}
                sx={{ 
                  width: '100%', 
                  mt: 2,
                  opacity: canEdit ? 1 : 0.6,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    background: 'white',
                    '& fieldset': { border: 'none' }
                  }
                }}
              />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={12} sm={6}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Requested Delivery"
                value={purchaseOrder.requested_delivery_date ? dayjs(purchaseOrder.requested_delivery_date) : null}
                onChange={(newValue) => canEdit && onUpdate({ requested_delivery_date: newValue ? newValue.format('YYYY-MM-DD') : '' })}
                disabled={!canEdit}
                sx={{ 
                  width: '100%', 
                  mt: 2,
                  opacity: canEdit ? 1 : 0.6,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    background: 'white',
                    '& fieldset': { border: 'none' }
                  }
                }}
              />
            </LocalizationProvider>
          </Grid>
        </Grid>
      </SectionPaper>
      )}

      {showSummary && (
      <SectionPaper sx={{ width: '100%', mt: 3 }}>
        <SectionTitle variant="h6">
          <MoneyIcon />
          Summary
        </SectionTitle>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TotalRow>
              <Typography variant="body1" fontWeight={500}>Subtotal:</Typography>
              <SummaryValue variant="body1" fontWeight={500}>
                {formatCurrency(purchaseOrder.subtotal)}
              </SummaryValue>
            </TotalRow>

            {/* Discount */}
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" sx={{ mr: 1, fontWeight: 500 }}>Discount:</Typography>
                <NumberTextField
                  type="number"
                  value={purchaseOrder.discount_percent || ''}
                  onChange={(e) => canEdit && onUpdate({ discount_percent: e.target.value })}
                  InputProps={discountType === 'percent'
                    ? { endAdornment: <InputAdornment position="end">%</InputAdornment> }
                    : { startAdornment: <InputAdornment position="start">$</InputAdornment> }
                  }
                  size="small"
                  disabled={!canEdit}
                  sx={{ width: '130px', opacity: canEdit ? 1 : 0.6 }}
                  inputProps={{ min: 0, max: discountType === 'percent' ? 100 : undefined, step: discountType === 'percent' ? 0.1 : 0.01 }}
                />
                <ToggleButtonGroup
                  value={discountType}
                  exclusive
                  size="small"
                  disabled={!canEdit}
                  onChange={(_, val) => { if (val && canEdit) onUpdate({ discount_type: val, discount_percent: 0 }); }}
                  sx={{ height: 32 }}
                >
                  <ToggleButton value="percent" sx={{ px: 1.5, fontSize: '0.75rem', textTransform: 'none' }}>%</ToggleButton>
                  <ToggleButton value="flat" sx={{ px: 1.5, fontSize: '0.75rem', textTransform: 'none' }}>$</ToggleButton>
                </ToggleButtonGroup>
              </Box>
              <SummaryValue variant="body1" color="error.main" fontWeight={500}>
                -{formatCurrency(purchaseOrder.discount_amount)}
              </SummaryValue>
            </Box>

            {/* Tax */}
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ mr: 2, fontWeight: 500 }}>Tax Rate:</Typography>
                <NumberTextField
                  type="number"
                  value={purchaseOrder.tax_rate || ''}
                  onChange={(e) => canEdit && onUpdate({ tax_rate: e.target.value })}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>
                  }}
                  size="small"
                  disabled={!canEdit}
                  sx={{ width: '130px', opacity: canEdit ? 1 : 0.6 }}
                  inputProps={{ min: 0, step: 0.1 }}
                />
              </Box>
              <SummaryValue variant="body1" fontWeight={500}>
                +{formatCurrency(purchaseOrder.tax_amount)}
              </SummaryValue>
            </Box>

            {/* Shipping */}
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ mr: 2, fontWeight: 500 }}>Shipping:</Typography>
                <NumberTextField
                  type="number"
                  value={purchaseOrder.shipping_fee || ''}
                  onChange={(e) => canEdit && onUpdate({ shipping_fee: e.target.value })}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>
                  }}
                  size="small"
                  disabled={!canEdit}
                  sx={{ width: '130px', opacity: canEdit ? 1 : 0.6 }}
                  inputProps={{ min: 0, step: 0.01 }}
                />
              </Box>
              <SummaryValue variant="body1" fontWeight={500}>
                +{formatCurrency(purchaseOrder.shipping_fee)}
              </SummaryValue>
            </Box>

            {/* Handling Fee */}
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ mr: 2, fontWeight: 500 }}>Handling:</Typography>
                <NumberTextField
                  type="number"
                  value={purchaseOrder.handling_fee || ''}
                  onChange={(e) => canEdit && onUpdate({ handling_fee: e.target.value })}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>
                  }}
                  size="small"
                  disabled={!canEdit}
                  sx={{ width: '130px', opacity: canEdit ? 1 : 0.6 }}
                  inputProps={{ min: 0, step: 0.01 }}
                />
              </Box>
              <SummaryValue variant="body1" fontWeight={500}>
                +{formatCurrency(purchaseOrder.handling_fee)}
              </SummaryValue>
            </Box>

            <Divider sx={{ my: 2, borderWidth: '1px', borderColor: alpha(theme.palette.grey[400], 0.8) }} />

            {/* Total Before Credit */}
            <TotalRow>
              <Typography variant="h6" fontWeight="500" sx={{ color: 'primary.main' }}>Total:</Typography>
              <Typography variant="h6" color="primary.main" fontWeight="500">
                {formatCurrency(purchaseOrder.total_before_credit)}
              </Typography>
            </TotalRow>

          </Grid>
        </Grid>
      </SectionPaper>
      )}

      {showPaymentTerms && (
      <SectionPaper sx={{ width: '100%', mt: 3 }}>
        <SectionTitle variant="h6">
          <MoneyIcon />
          Payment Terms
        </SectionTitle>
        
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <Autocomplete
              freeSolo
              value={purchaseOrder.payment_terms || ''}
              onChange={(event, newValue) => {
                canEdit && onUpdate({ payment_terms: newValue || '' });
              }}
              onInputChange={(event, newInputValue) => {
                canEdit && onUpdate({ payment_terms: newInputValue || '' });
              }}
              disabled={!canEdit}
              options={[
                'Due on Receipt',
                'Net 10',
                'Net 15',
                'Net 30',
                'Net 45',
                'Net 60',
                'Net 90',
                'COD',
                'Prepaid'
              ]}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Payment Terms"
                  placeholder="Auto-fetched from Order Creation"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      background: 'white',
                      opacity: canEdit ? 1 : 0.6,
                      '& fieldset': { border: 'none' }
                    }
                  }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Autocomplete
              freeSolo
              value={purchaseOrder.payment_method || ''}
              onChange={(event, newValue) => {
                canEdit && onUpdate({ payment_method: newValue || '' });
              }}
              onInputChange={(event, newInputValue) => {
                canEdit && onUpdate({ payment_method: newInputValue || '' });
              }}
              disabled={!canEdit}
              options={[
                'Check',
                'Credit Card',
                'Bank Transfer',
                'Wire Transfer',
                'Cash',
                'PayPal',
                'Other'
              ]}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Preferred Payment Method"
                  placeholder="Wire Transfer"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      background: 'white',
                      opacity: canEdit ? 1 : 0.6,
                      '& fieldset': { border: 'none' }
                    }
                  }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              label="Late Fee"
              type="number"
              value={purchaseOrder.late_fee_rate || ''}
              onChange={(e) => canEdit && onUpdate({ late_fee_rate: e.target.value })}
              fullWidth
              disabled={!canEdit}
              inputProps={{ min: 0, max: 100, step: 0.1 }}
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
                sx: {
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  background: 'white',
                  opacity: canEdit ? 1 : 0.6,
                  '& fieldset': { border: 'none' }
                }
              }}
              helperText="Monthly late fee rate"
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              label="Early Payment"
              type="number"
              value={purchaseOrder.early_payment_discount || ''}
              onChange={(e) => canEdit && onUpdate({ early_payment_discount: e.target.value })}
              fullWidth
              disabled={!canEdit}
              inputProps={{ min: 0, max: 100, step: 0.1 }}
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
                sx: {
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  background: 'white',
                  opacity: canEdit ? 1 : 0.6,
                  '& fieldset': { border: 'none' }
                }
              }}
              helperText="Discount for early payment"
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              label="Early Payment"
              type="number"
              value={purchaseOrder.early_payment_days || ''}
              onChange={(e) => canEdit && onUpdate({ early_payment_days: e.target.value })}
              fullWidth
              disabled={!canEdit}
              inputProps={{ min: 0, step: 1 }}
              InputProps={{
                sx: {
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  background: 'white',
                  opacity: canEdit ? 1 : 0.6,
                  '& fieldset': { border: 'none' }
                }
              }}
              helperText="Days for discount"
            />
          </Grid>
        </Grid>
      </SectionPaper>
      )}

      {showNotes && (
      <SectionPaper sx={{ width: '100%', mt: 3 }}>
        <SectionTitle variant="h6">
          <EditIcon />
          Additional Notes
        </SectionTitle>
        
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              label="Notes to Customer"
              multiline
              rows={4}
              value={purchaseOrder.customer_notes || ''}
              onChange={(e) => canEdit && onUpdate({ customer_notes: e.target.value })}
              fullWidth
              disabled={!canEdit}
              placeholder="Enter any additional notes..."
              InputProps={{
                sx: {
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  background: 'white',
                  opacity: canEdit ? 1 : 0.6,
                  '& fieldset': { border: 'none' }
                }
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Special Instructions"
              multiline
              rows={3}
              value={purchaseOrder.special_instructions || ''}
              onChange={(e) => canEdit && onUpdate({ special_instructions: e.target.value })}
              fullWidth
              disabled={!canEdit}
              placeholder="Packing, shipping, or handling instructions..."
              InputProps={{
                sx: {
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  background: 'white',
                  opacity: canEdit ? 1 : 0.6,
                  '& fieldset': { border: 'none' }
                }
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Internal Notes"
              multiline
              rows={2}
              value={purchaseOrder.internal_notes || ''}
              onChange={(e) => canEdit && onUpdate({ internal_notes: e.target.value })}
              fullWidth
              disabled={!canEdit}
              placeholder="Internal notes (not visible to customer)..."
              InputProps={{
                sx: {
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  background: 'white',
                  opacity: canEdit ? 1 : 0.6,
                  '& fieldset': { border: 'none' }
                }
              }}
            />
          </Grid>
        </Grid>
      </SectionPaper>
      )}
    </>
  );
};

export default PISummarySection;